import { FC, useEffect, useMemo, useState } from 'react';

import { Autocomplete, TextField } from '@mui/material';
import { getActiveSubsidiaries } from '@shared/services/api/subsidiaryService';
import { getLabel } from '@shared/utils';

interface SubsidiaryOption {
  suc_id: number;
  sucursal: string;
}

interface SubsidiaryFilterSelectorProps {
  value?: number | null;
  disabled?: boolean;
  onSearch: (key: string, value: string | number | null) => void;
}

const SubsidiaryFilterSelector: FC<SubsidiaryFilterSelectorProps> = ({
  value = null,
  disabled = false,
  onSearch,
}) => {
  const [subsidiaries, setSubsidiaries] = useState<SubsidiaryOption[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const fetchSubsidiaries = async () => {
      setLoading(true);
      try {
        const result = await getActiveSubsidiaries();
        if (!cancelled) {
          setSubsidiaries(result?.data ?? []);
        }
      } catch (error) {
        console.error('Error fetching subsidiaries:', error);
        if (!cancelled) {
          setSubsidiaries([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchSubsidiaries();

    return () => {
      cancelled = true;
    };
  }, []);

  const selectedOption = useMemo(
    () => subsidiaries.find((subsidiary) => subsidiary.suc_id === value) ?? null,
    [subsidiaries, value]
  );

  const handleChange = (nextValue: SubsidiaryOption | null) => {
    if (!nextValue) {
      onSearch('subsidiary_id', null);
      onSearch('subsidiary_name', '');
      return;
    }

    onSearch('subsidiary_id', nextValue.suc_id);
    onSearch('subsidiary_name', nextValue.sucursal);
  };

  return (
    <Autocomplete
      options={subsidiaries}
      value={selectedOption}
      loading={loading}
      disabled={disabled}
      clearOnEscape
      getOptionLabel={(option) => option.sucursal ?? ''}
      isOptionEqualToValue={(option, selected) => option.suc_id === selected?.suc_id}
      onChange={(_, nextValue) => handleChange(nextValue)}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          label={getLabel('subsidiary')}
          placeholder="Todos"
          slotProps={{
            inputLabel: { shrink: true },
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#fff',
              borderRadius: '12px',
            },
          }}
        />
      )}
    />
  );
};

export default SubsidiaryFilterSelector;
